import { call, put, take, takeLatest } from 'redux-saga/effects';

import {
  CASE_DOWNLOAD_FILES_BEGIN,
  CASE_DOWNLOAD_FILES_SUCCESS,
  CASE_DOWNLOAD_FILES_FAILURE,
  CASE_DOWNLOAD_FILES_DISMISS_FEEDBACK,
} from './constants';
import api from 'common/api';
import { withCurrentCaseId } from '../../../common/selectors';
import {
  createPromiseAction,
  resolvePromiseAction,
  rejectPromiseAction,
} from '@adobe/redux-saga-promise';

const isJsonBlob = (blob) =>
  typeof Blob !== 'undefined' &&
  blob instanceof Blob &&
  typeof blob.type === 'string' &&
  blob.type.includes('application/json');

const QUEUED_RESPONSE_MAX_BYTES = 256 * 1024;

const canContainQueuedPayload = (document) =>
  !!document?.blob &&
  typeof document.blob.text === 'function' &&
  (isJsonBlob(document.blob) || document.blob.size <= QUEUED_RESPONSE_MAX_BYTES);

function* getQueuedDownloadResponse(document) {
  if (!canContainQueuedPayload(document)) {
    return null;
  }

  try {
    const bodyText = yield call([document.blob, document.blob.text]);
    const parsedBody = JSON.parse(bodyText);

    if (String(parsedBody?.status || '').trim().toLowerCase() === 'queued') {
      return parsedBody;
    }
  } catch (error) {
    console.error('Failed to parse queued download response', error);
  }

  return null;
}

export const downloadFiles = createPromiseAction(CASE_DOWNLOAD_FILES_BEGIN);
const DOWNLOAD_SCOPE_FILE_IDS = 'fileIds';
const DOWNLOAD_SCOPE_FOLDER = 'folder';
const DOWNLOAD_SCOPE_FOLDER_TREE = 'folderTree';
const DOWNLOAD_NAMING_STRATEGY_DEFAULT = 'default';
const DOWNLOAD_NAMING_STRATEGY_ORIGINAL = 'original';
const DOWNLOAD_NAMING_STRATEGY_FOLDER_SPECIFIC_NAME = 'folderSpecificName';
const DOWNLOAD_NAMING_STRATEGY_BUNDLE_TAB_ONLY = 'bundleTabOnly';
const DOWNLOAD_NAMING_STRATEGY_BUNDLE_TAB_FILENAME = 'bundleTabFilename';

const normalizeId = (value) => (value === null || typeof value === 'undefined' ? '' : String(value).trim());
const normalizeKey = (value) => normalizeId(value).toLowerCase();

const normalizeIdList = (values = []) =>
  Array.from(
    new Set(
      (values || [])
        .map((value) => normalizeId(value))
        .filter(Boolean),
    ),
  );

const resolveScopeType = ({ scopeType, fileIds, folderId, folderHasChildren }) => {
  if (
    [DOWNLOAD_SCOPE_FILE_IDS, DOWNLOAD_SCOPE_FOLDER, DOWNLOAD_SCOPE_FOLDER_TREE].includes(scopeType)
  ) {
    return scopeType;
  }

  if (fileIds.length) {
    return DOWNLOAD_SCOPE_FILE_IDS;
  }

  if (folderId) {
    return folderHasChildren ? DOWNLOAD_SCOPE_FOLDER_TREE : DOWNLOAD_SCOPE_FOLDER;
  }

  return DOWNLOAD_SCOPE_FILE_IDS;
};

const resolveNamingStrategy = ({ namingStrategy, namingMode, filenameSelector, fileNameSelector }) => {
  const normalizedNamingStrategy = normalizeKey(namingStrategy || namingMode);
  if (normalizedNamingStrategy === DOWNLOAD_NAMING_STRATEGY_DEFAULT) {
    return DOWNLOAD_NAMING_STRATEGY_DEFAULT;
  }
  if (normalizedNamingStrategy === DOWNLOAD_NAMING_STRATEGY_ORIGINAL) {
    return DOWNLOAD_NAMING_STRATEGY_ORIGINAL;
  }
  if (
    normalizedNamingStrategy === normalizeKey(DOWNLOAD_NAMING_STRATEGY_FOLDER_SPECIFIC_NAME) ||
    normalizedNamingStrategy === 'folderspecific'
  ) {
    return DOWNLOAD_NAMING_STRATEGY_FOLDER_SPECIFIC_NAME;
  }
  if (
    normalizedNamingStrategy === normalizeKey(DOWNLOAD_NAMING_STRATEGY_BUNDLE_TAB_ONLY) ||
    normalizedNamingStrategy === 'bundletabonly' ||
    normalizedNamingStrategy === 'bundletab'
  ) {
    return DOWNLOAD_NAMING_STRATEGY_BUNDLE_TAB_ONLY;
  }
  if (
    normalizedNamingStrategy === normalizeKey(DOWNLOAD_NAMING_STRATEGY_BUNDLE_TAB_FILENAME) ||
    normalizedNamingStrategy === 'bundletabfilename'
  ) {
    return DOWNLOAD_NAMING_STRATEGY_BUNDLE_TAB_FILENAME;
  }

  const resolvedFileNameSelector = normalizeKey(filenameSelector || fileNameSelector);
  if (resolvedFileNameSelector === 'original') {
    return DOWNLOAD_NAMING_STRATEGY_ORIGINAL;
  }
  if (resolvedFileNameSelector === 'bundletab' || resolvedFileNameSelector === 'bundletabonly') {
    return DOWNLOAD_NAMING_STRATEGY_BUNDLE_TAB_ONLY;
  }
  if (resolvedFileNameSelector === 'bundletabfilename') {
    return DOWNLOAD_NAMING_STRATEGY_BUNDLE_TAB_FILENAME;
  }
  if (
    resolvedFileNameSelector === normalizeKey(DOWNLOAD_NAMING_STRATEGY_FOLDER_SPECIFIC_NAME) ||
    resolvedFileNameSelector === 'folderspecific'
  ) {
    return DOWNLOAD_NAMING_STRATEGY_FOLDER_SPECIFIC_NAME;
  }

  // Unsupported legacy selectors (e.g. discoveryId/externalId/trialviewId) map to default.
  return DOWNLOAD_NAMING_STRATEGY_DEFAULT;
};

const resolvePrependBundleTab = ({
  prependBundleTab,
  useTabNumbers,
  namingStrategy,
  namingMode,
  filenameSelector,
  fileNameSelector,
}) => {
  if (typeof prependBundleTab === 'boolean') {
    return prependBundleTab;
  }

  const normalizedNamingStrategy = normalizeKey(namingStrategy || namingMode);
  if (
    normalizedNamingStrategy === normalizeKey(DOWNLOAD_NAMING_STRATEGY_BUNDLE_TAB_FILENAME) ||
    normalizedNamingStrategy === 'bundletabfilename'
  ) {
    return true;
  }

  const resolvedFileNameSelector = normalizeKey(filenameSelector || fileNameSelector);
  if (resolvedFileNameSelector === 'bundletabfilename') {
    return true;
  }

  return !!useTabNumbers;
};

const buildNaming = ({
  namingStrategy,
  namingMode,
  filenameSelector,
  fileNameSelector,
  prependBundleTab,
  useTabNumbers,
  folderId,
}) => {
  let resolvedNamingStrategy = resolveNamingStrategy({
    namingStrategy,
    namingMode,
    filenameSelector,
    fileNameSelector,
  });
  let resolvedPrependBundleTab = resolvePrependBundleTab({
    prependBundleTab,
    useTabNumbers,
    namingStrategy,
    namingMode,
    filenameSelector,
    fileNameSelector,
  });

  // Legacy bundleTabFilename maps to folderSpecificName + prependBundleTab=true.
  if (resolvedNamingStrategy === DOWNLOAD_NAMING_STRATEGY_BUNDLE_TAB_FILENAME) {
    resolvedNamingStrategy = DOWNLOAD_NAMING_STRATEGY_FOLDER_SPECIFIC_NAME;
    resolvedPrependBundleTab = true;
  }

  const normalizedFolderId = normalizeId(folderId);
  if (
    !normalizedFolderId &&
    (
      resolvedNamingStrategy === DOWNLOAD_NAMING_STRATEGY_FOLDER_SPECIFIC_NAME ||
      resolvedNamingStrategy === DOWNLOAD_NAMING_STRATEGY_BUNDLE_TAB_ONLY ||
      resolvedPrependBundleTab
    )
  ) {
    resolvedNamingStrategy = DOWNLOAD_NAMING_STRATEGY_DEFAULT;
    resolvedPrependBundleTab = false;
  }

  const naming = {
    strategy: resolvedNamingStrategy,
  };

  if (resolvedPrependBundleTab) {
    naming.prependBundleTab = true;
  }

  if (
    normalizedFolderId &&
    (
      resolvedNamingStrategy === DOWNLOAD_NAMING_STRATEGY_FOLDER_SPECIFIC_NAME ||
      resolvedNamingStrategy === DOWNLOAD_NAMING_STRATEGY_BUNDLE_TAB_ONLY ||
      resolvedPrependBundleTab
    )
  ) {
    naming.folderId = normalizedFolderId;
  }

  return naming;
};

export function dismissDownloadFilesFeedback() {
  return {
    type: CASE_DOWNLOAD_FILES_DISMISS_FEEDBACK,
  };
}

export function* doDownloadFiles(action) {
  const {
    payload: {
      caseId,
      fileIds,
      fileId,
      folderId,
      variant,
      version,
      filenameSelector,
      fileNameSelector,
      scopeType,
      namingStrategy,
      namingMode,
      prependBundleTab,
      folderHasChildren,
      burnId,
      burnDiscoveryId,
      burnDisclosureId,
      burnExhibitNumber,
      burnExternalId,
      burncrossref,
      burnCrossRef,
      hyperlink,
      publichyperlink,
      includeMerge,
      useTabNumbers,
      useBundleTabPage,
    },
  } = action;

  const resolvedFileIds = normalizeIdList(fileIds || (fileId ? [fileId] : []));
  const normalizedFolderId = normalizeId(folderId);
  const resolvedScopeType = resolveScopeType({
    scopeType,
    fileIds: resolvedFileIds,
    folderId: normalizedFolderId,
    folderHasChildren: !!folderHasChildren,
  });
  const naming = buildNaming({
    namingStrategy,
    namingMode,
    filenameSelector,
    fileNameSelector,
    prependBundleTab,
    useTabNumbers,
    folderId: normalizedFolderId,
  });
  const resolvedBurnCrossRef =
    typeof burncrossref !== 'undefined' ? burncrossref : burnCrossRef;
  const requestBody = {
    scope: {
      type: resolvedScopeType,
      fileIds: resolvedScopeType === DOWNLOAD_SCOPE_FILE_IDS ? resolvedFileIds : [],
      folderId:
        resolvedScopeType === DOWNLOAD_SCOPE_FOLDER || resolvedScopeType === DOWNLOAD_SCOPE_FOLDER_TREE
          ? normalizedFolderId
          : '',
    },
    naming,
    variant: variant || 'noRing',
    version: version || 'original',
    burnId: !!burnId,
    burnDiscoveryId: !!burnDiscoveryId,
    burnDisclosureId: !!burnDisclosureId,
    burnExhibitNumber: !!burnExhibitNumber,
    burnExternalId: !!burnExternalId,
    burncrossref: !!resolvedBurnCrossRef,
    hyperlink: !!hyperlink,
    publichyperlink: !!publichyperlink,
    includeMerge: !!includeMerge,
    useTabNumbers: !!useTabNumbers,
    useBundleTabPage: !!useBundleTabPage,
  };

  yield put({
    type: CASE_DOWNLOAD_FILES_BEGIN,
    feedback: {
      message: 'feedback.downloadFilesPending',
      pending: true,
    },
  });

  try {
    const channel = yield call(api.createDownloadFileChannel, `/cases/${caseId}/files/downloads`, false, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    while (true) {
      const { error, success, response: document } = yield take(channel);

      if (error) {
        yield put({
          type: CASE_DOWNLOAD_FILES_FAILURE,
          feedback: {
            message: 'feedback.downloadFilesFailure',
            error: error,
            retryAction: action,
          },
        });
        return yield call(rejectPromiseAction, action, error);
      }

      if (success) {
        const queuedDownloadResponse = yield call(getQueuedDownloadResponse, document);
        const resolvedResponse = queuedDownloadResponse || document;
        const isQueuedDownload = !!queuedDownloadResponse;

        yield put({
          type: CASE_DOWNLOAD_FILES_SUCCESS,
          feedback: {
            message: isQueuedDownload ? 'feedback.downloadFilesPending' : 'feedback.downloadFilesSuccess',
            ...(isQueuedDownload && { info: true }),
          },
          data: resolvedResponse,
        });
        yield call(resolvePromiseAction, action, resolvedResponse);
        return;
      }
    }
  } catch (error) {
    yield put({
      type: CASE_DOWNLOAD_FILES_FAILURE,
      feedback: {
        message: 'feedback.downloadFilesFailure',
        error: error.message || error,
        retryAction: action,
      },
    });
    yield call(rejectPromiseAction, action, error);
  }
}

/*
  Alternatively you may use takeEvery.

  takeLatest does not allow concurrent requests. If an action gets
  dispatched while another is already pending, that pending one is cancelled
  and only the latest one will be run.
*/
export function* watchDownloadFiles() {
  yield takeLatest(downloadFiles, withCurrentCaseId(doDownloadFiles));
}

// Redux reducer
export function reducer(state, action) {
  switch (action.type) {
    case CASE_DOWNLOAD_FILES_BEGIN + '.TRIGGER':
      return {
        ...state,
        downloadFilesPending: true,
        downloadFilesFeedback: null,
      };

    case CASE_DOWNLOAD_FILES_SUCCESS:
      return {
        ...state,
        downloadFilesPending: false,
        downloadFilesFeedback: action.feedback,
      };

    case CASE_DOWNLOAD_FILES_FAILURE:
      return {
        ...state,
        downloadFilesPending: false,
        downloadFilesFeedback: action.feedback,
      };

    case CASE_DOWNLOAD_FILES_DISMISS_FEEDBACK:
      return {
        ...state,
        downloadFilesFeedback: null,
      };

    default:
      return state;
  }
}
